/**
 * `effectiveRoster` — the one function every staff type's roster passes
 * through, so the matching logic downstream cannot tell a block paramedic
 * from an FWA one (`CLAUDE.md` "The roster engine", `brief.md`).
 *
 *     effectiveRoster(user, range) =
 *           derivedBlockShifts(user.block)     // computed, free
 *         − suppressed by day_exceptions       // leave / unavailable / not-working
 *         − shifts swapped away
 *         + explicit shift rows                // FWA entries, recurrences, swap-acquired
 *
 * **Five ordered steps. The order is the specification** — read this comment
 * against the numbered blocks in the function body below; they are written
 * in the same order on purpose.
 *
 * 1. **Derive.** `block === null` is the FWA path — no derived shifts, and it
 *    must run through this same function rather than a separate one, so a
 *    flexible-working user and a block user look identical to every caller.
 *    Otherwise, `derivedBlockShifts(block, range, config)`.
 *
 * 2. **Suppress by exception.** A derived shift whose date carries a
 *    `DayException` is dropped. All three kinds — `LEAVE`, `UNAVAILABLE`,
 *    `NOT_WORKING` — suppress identically: they differ in what they *mean* to
 *    a human, not in whether the person is working that day.
 *
 *    **Ruling — exceptions suppress derived shifts only, never explicit
 *    ones. Do not change this.** A `LEAVE` day carrying an explicit shift
 *    (an FWA entry, or a shift just acquired in a swap) is a genuine
 *    contradiction, and silently hiding the explicit shift would hide that
 *    conflict from the person who most needs to see it. This function's job
 *    is to report the roster as it *is*; Phase 6's rules hard-block creating
 *    that contradiction going forward, and can report it when it already
 *    exists. See fixture `LEAVE_OVER_EXPLICIT`, which pins this.
 *
 * 3. **Drop departed explicit rows.** Explicit shifts with status
 *    `SWAPPED_AWAY` or `CANCELLED` never appear in the output.
 *
 * 4. **Replace, don't duplicate.** An explicit row on the same `(date, code)`
 *    as a derived shift *replaces* it — that is what materialisation means:
 *    advertising or swapping a derived shift gives it a database row, and the
 *    same shift must not then show up twice.
 *
 *    **This is the single subtlest line in the phase, and it is why steps 3
 *    and 4 are numbered in this order rather than folded together.** The
 *    matching in this step is done against the *full* explicit list — every
 *    row that survived step 1's range filter, including the ones step 3 just
 *    dropped — not only the survivors of step 3. Matching against the
 *    survivors only would let a swapped-away shift's derived twin reappear:
 *    the explicit row is gone (step 3 removed it), so a same-day, same-code
 *    derived shift would no longer find anything to match against, and would
 *    silently take its place. A paramedic would see a shift they no longer
 *    work, and the app would tell them to turn up for it. Fixture
 *    `SWAPPED_AWAY_2B` pins this by asserting zero shifts on that date, not
 *    one.
 *
 * 5. **Add and sort.** Surviving explicit rows (step 3's output) join the
 *    result as `ShiftInstance`s with `source: 'EXPLICIT'`, carrying their
 *    `id` as `shiftId` and their `origin`. The merged list sorts by date,
 *    then by the shift's `startMin` from `config.shifts` — never
 *    alphabetically by code, which would order `AM, D, N, PM` and put a
 *    night shift before the evening one that actually precedes it on the
 *    same day.
 *
 * **Range.** Explicit shifts and exceptions outside `input.range` are
 * ignored entirely — not matched against, not returned. The function reports
 * only what falls inside the requested window.
 *
 * **No verdicts.** This module never decides that something is a conflict, a
 * warning or a block — it reports what the roster *is*. `src/rules/` is
 * where a verdict gets made, starting in Phase 6.
 *
 * **Purity.** No DOM, no React, no Supabase, no database, no async, no I/O.
 * Exceptions and explicit shifts arrive as plain arrays; `RosterConfig`
 * arrives as an argument, the same as `roster.ts` — this module imports
 * nothing from `src/data/`. Phase 3 supplies real Postgres rows through the
 * same two array parameters and writes no new merge logic, which is the
 * entire point of building this algebra now, while it can still be pinned
 * with hand-written fixtures instead of a database and a UI.
 */

import { dayNumber } from './civil'
import { derivedBlockShifts } from './roster'
import type {
  BlockCode,
  DateRange,
  DayException,
  ExplicitShift,
  IsoDate,
  RosterConfig,
  ShiftCode,
  ShiftInstance,
} from './types'

export interface EffectiveRosterInput {
  block: BlockCode | null
  range: DateRange
  exceptions: readonly DayException[]
  explicit: readonly ExplicitShift[]
}

/** Explicit statuses that mean "this row no longer represents a real shift". */
const DEPARTED_STATUSES: ReadonlySet<ExplicitShift['status']> = new Set([
  'SWAPPED_AWAY',
  'CANCELLED',
])

/** Whether `date` falls inside `range`, both ends inclusive. */
function isInRange(date: IsoDate, range: DateRange): boolean {
  const day = dayNumber(date)
  return day >= dayNumber(range.from) && day <= dayNumber(range.to)
}

/**
 * A shift code's configured start time, for sorting same-day shifts in the
 * order they actually start rather than alphabetically.
 *
 * Throws on an unconfigured code for the same reason `shiftCodeForCycleDay`
 * throws in `roster.ts`: a `ShiftInstance` with a code the config doesn't
 * know is a bug upstream, and silently sorting it last would hide that.
 */
function startMinFor(code: ShiftCode, config: RosterConfig): number {
  const shift = config.shifts.find((candidate) => candidate.code === code)
  if (shift === undefined) {
    throw new RangeError(`No roster shift configured for code ${JSON.stringify(code)}`)
  }
  return shift.startMin
}

export function effectiveRoster(
  input: EffectiveRosterInput,
  config: RosterConfig,
): ShiftInstance[] {
  const { block, range, exceptions, explicit } = input

  // Step 1 — derive.
  const derived = block === null ? [] : derivedBlockShifts(block, range, config)

  // Step 2 — suppress by exception. All three kinds suppress identically,
  // and only against derived shifts — see the ruling in the header comment.
  const exceptionDates = new Set(
    exceptions.filter((exception) => isInRange(exception.date, range)).map((e) => e.date),
  )
  const afterExceptions = derived.filter((shift) => !exceptionDates.has(shift.date))

  // Explicit rows inside the range, before step 3 drops the departed ones.
  // Step 4 matches against *this* full list, not the step-3 survivors —
  // that is what stops a swapped-away shift's derived twin reappearing.
  const explicitInRange = explicit.filter((row) => isInRange(row.date, range))

  // Step 3 — drop departed explicit rows.
  const survivingExplicit = explicitInRange.filter((row) => !DEPARTED_STATUSES.has(row.status))

  // Step 4 — replace, don't duplicate. Matched against `explicitInRange`
  // (full list, including SWAPPED_AWAY/CANCELLED rows), never against
  // `survivingExplicit`. See the header comment's step 4 for why the order
  // matters.
  const derivedFinal = afterExceptions.filter(
    (shift) =>
      !explicitInRange.some((row) => row.date === shift.date && row.code === shift.code),
  )

  // Step 5 — add and sort.
  const explicitInstances: ShiftInstance[] = survivingExplicit.map((row) => ({
    date: row.date,
    code: row.code,
    source: 'EXPLICIT',
    shiftId: row.id,
    origin: row.origin,
  }))

  const merged = [...derivedFinal, ...explicitInstances]
  merged.sort((a, b) => {
    const dayDelta = dayNumber(a.date) - dayNumber(b.date)
    if (dayDelta !== 0) return dayDelta
    return startMinFor(a.code, config) - startMinFor(b.code, config)
  })
  return merged
}
